// Display Devices — Apple TV floor board credentials (ADR 0004).
//
// Pairing (QM decision 2026-10-04): the Apple TV shows a code; an admin enters
// it here with a facility and a label. The board has no controls (D-TV-01),
// so nothing is typed on the TV. An installer path with a one-time code typed
// at install time remains under "Installer: one-time code".
// Revoking a display ends every token it holds at once. A display can only
// read its own facility's board. Labels name a place, never a person (D-TV-07).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, Stack, Typography, Breadcrumbs, Paper, Box, Alert, Button, TextField, MenuItem,
  TableHead, TableRow, TableCell, TableBody, TableContainer, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import PageGuide from "../../components/PageGuide";
import { facilitiesApi } from "../../utils/facilitiesApi";
import { displayDevicesApi } from "../../utils/displayDevicesApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const fmt = (d) => (d ? new Date(d).toLocaleString() : "");
const errText = (err, fallback) => err?.response?.data?.message || err?.response?.data?.code || err?.message || fallback;

function statusOf(d) {
  if (d.revokedAt) return { label: `Revoked ${fmt(d.revokedAt)}`, color: "default" };
  if (d.pairedAt) return { label: `Paired ${fmt(d.pairedAt)}`, color: "success" };
  return { label: "Waiting to pair", color: "warning" };
}

export default function DisplayDevices() {
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null); // { label, pairingCode, pairingExpiresAt }
  const [confirm, setConfirm] = useState(null); // { kind: 'revoke'|'recode', device }
  const [tvCode, setTvCode] = useState("");
  const [showInstaller, setShowInstaller] = useState(false);

  const facilityName = useMemo(() => {
    const m = new Map(facilities.map((f) => [String(f.id), f.facility_name || f.name || `Facility ${f.id}`]));
    return (id) => m.get(String(id)) || `Facility ${id}`;
  }, [facilities]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, dRes] = await Promise.all([facilitiesApi.list(), displayDevicesApi.list()]);
      const f = fRes?.data?.data || fRes?.data || [];
      setFacilities(Array.isArray(f) ? f : []);
      setDevices(dRes?.data?.devices || []);
      if (!facilityId && Array.isArray(f) && f.length === 1) setFacilityId(String(f[0].id));
    } catch (err) {
      toast.error(errText(err, "Failed to load display devices"));
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  // After an approval the TV claims its token a second or two later, so the
  // first reload still shows "Waiting to pair". Re-check briefly afterwards.
  const [recheckUntil, setRecheckUntil] = useState(0);
  useEffect(() => {
    if (!recheckUntil) return undefined;
    const timers = [3000, 8000, 20000].map((ms) => setTimeout(() => { if (Date.now() <= recheckUntil) load(); }, ms));
    return () => timers.forEach(clearTimeout);
  }, [recheckUntil, load]);

  const labelOk = label.trim().length >= 2 && label.trim().length <= 80;
  const tvCodeClean = tvCode.toUpperCase().replace(/[^0-9A-Z]/g, "");
  const tvCodeOk = tvCodeClean.length === 8;

  const approveTv = async () => {
    setBusy(true);
    try {
      await displayDevicesApi.approveTvCode({ userCode: tvCodeClean, facilityId: Number(facilityId), label: label.trim() });
      toast.success(`${label.trim()} approved. The TV starts showing the board within a few seconds.`);
      setTvCode(""); setLabel("");
      await load();
      setRecheckUntil(Date.now() + 25000);
    } catch (err) {
      const c = err?.response?.data?.code;
      toast.error(c === "CODE_NOT_FOUND"
        ? "That code is not waiting for approval. Check the TV: codes change every 10 minutes."
        : errText(err, "Could not approve the TV code"));
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    setBusy(true);
    try {
      const res = await displayDevicesApi.create({ facilityId: Number(facilityId), label: label.trim() });
      setCode({ label: label.trim(), pairingCode: res.data.pairingCode, pairingExpiresAt: res.data.pairingExpiresAt });
      setLabel("");
      await load();
    } catch (err) {
      toast.error(errText(err, "Could not add the display"));
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    const { kind, device } = confirm;
    setConfirm(null);
    setBusy(true);
    try {
      if (kind === "revoke") {
        await displayDevicesApi.revoke(device.id);
        toast.success(`${device.label} revoked. It stopped showing data immediately.`);
      } else {
        const res = await displayDevicesApi.newPairingCode(device.id);
        setCode({ label: device.label, pairingCode: res.data.pairingCode, pairingExpiresAt: res.data.pairingExpiresAt });
      }
      await load();
    } catch (err) {
      toast.error(errText(err, "The change was not made"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Helmet><title>Display Devices | SFM</title></Helmet>
      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">Display Devices</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ mb: 1 }}>Display Devices</Typography>
      <PageGuide
        why="Put the live floor board on a shop TV without giving that TV a login. A display can only read its own facility's board."
        who="An administrator setting up or retiring a wall-mounted Apple TV."
        what="The screens registered for each facility, whether they are paired, and when they were revoked."
        how="Open SFM Floor Board on the Apple TV. It shows a code. Enter that code here with the facility and a label, then approve. Revoke a display to cut it off at once."
        when="When a new screen goes up, a TV is replaced or reset, or a screen should stop showing data."
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Approve a TV code</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
          <TextField
            label="Code shown on the TV" value={tvCode} onChange={(e) => setTvCode(e.target.value)} sx={{ minWidth: 220 }}
            placeholder="ABCD-EFGH" inputProps={{ maxLength: 9, style: { fontFamily: "monospace", letterSpacing: 2, textTransform: "uppercase" } }}
            disabled={busy} helperText="8 characters. Codes change every 10 minutes."
          />
          <TextField
            select label="Facility" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}
            sx={{ minWidth: 260 }} disabled={busy || facilities.length === 0}
            helperText={facilities.length === 0 && !loading ? "No facilities yet. Add one under Facilities first." : " "}
          >
            {facilities.map((f) => <MenuItem key={f.id} value={String(f.id)}>{facilityName(f.id)}</MenuItem>)}
          </TextField>
          <TextField
            label="Display label" value={label} onChange={(e) => setLabel(e.target.value)} sx={{ minWidth: 320 }}
            placeholder="e.g. Bay 2 wall TV" inputProps={{ maxLength: 80 }} disabled={busy}
            helperText="Name the place, not a person."
          />
          <Button variant="contained" onClick={approveTv} disabled={busy || !tvCodeOk || !facilityId || !labelOk} sx={{ mt: { md: 1 } }}>
            Approve
          </Button>
        </Stack>
        <Button size="small" sx={{ mt: 1 }} onClick={() => setShowInstaller((v) => !v)}>
          {showInstaller ? "Hide installer option" : "Installer: one-time code"}
        </Button>
      </Paper>

      {showInstaller && (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Installer: add a display with a one-time code</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
          <TextField
            select label="Facility" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}
            sx={{ minWidth: 260 }} disabled={busy || facilities.length === 0}
            helperText={facilities.length === 0 && !loading ? "No facilities yet. Add one under Facilities first." : " "}
          >
            {facilities.map((f) => <MenuItem key={f.id} value={String(f.id)}>{facilityName(f.id)}</MenuItem>)}
          </TextField>
          <TextField
            label="Display label" value={label} onChange={(e) => setLabel(e.target.value)} sx={{ minWidth: 320 }}
            placeholder="e.g. Bay 2 wall TV" inputProps={{ maxLength: 80 }} disabled={busy}
            helperText="Name the place, not a person."
          />
          <Button variant="contained" onClick={add} disabled={busy || !facilityId || !labelOk} sx={{ mt: { md: 1 } }}>
            Add display
          </Button>
        </Stack>
      </Paper>
      )}

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <SmartTable tableId="display-devices" lockedColumns={["Display", "Status"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Display</TableCell>
                <TableCell>Facility</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.length === 0 && (
                <TableRow><TableCell colSpan={5}><Box sx={{ py: 2, color: "text.secondary" }}>No displays yet.</Box></TableCell></TableRow>
              )}
              {devices.map((d) => {
                const s = statusOf(d);
                return (
                  <TableRow key={d.id}>
                    <TableCell>{d.label}</TableCell>
                    <TableCell>{facilityName(d.facilityId)}</TableCell>
                    <TableCell><Chip size="small" label={s.label} color={s.color} variant="outlined" /></TableCell>
                    <TableCell>{fmt(d.createdAt)}</TableCell>
                    <TableCell align="right">
                      {!d.revokedAt && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => setConfirm({ kind: "recode", device: d })} disabled={busy}>New code</Button>
                          <Button size="small" color="error" onClick={() => setConfirm({ kind: "revoke", device: d })} disabled={busy}>Revoke</Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      {/* One-time pairing code. Shown once; the server keeps only its hash. */}
      <Dialog open={!!code} onClose={() => setCode(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Pairing code for {code?.label}</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2, textAlign: "center", fontFamily: "monospace", fontSize: 44, fontWeight: 700, letterSpacing: 4 }}>
            {code?.pairingCode}
          </Box>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Shown once. Expires {fmt(code?.pairingExpiresAt)} and works one time.
          </Alert>
          <DialogContentText>
            For installers only: this code is entered at install time, not on the board itself. If it expires, use New code on this page.
          </DialogContentText>
        </DialogContent>
        <DialogActions><Button onClick={() => setCode(null)} variant="contained">Done</Button></DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm?.kind === "revoke" ? `Revoke ${confirm?.device?.label}?` : `New code for ${confirm?.device?.label}?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.kind === "revoke"
              ? "The screen stops showing data immediately. This cannot be undone; to use the TV again, add it as a new display."
              : "The screen stops showing data. For an installer, this issues a one-time code. To pair a TV that shows a code instead, revoke this display and approve the code the TV shows."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button onClick={runConfirm} color={confirm?.kind === "revoke" ? "error" : "primary"} variant="contained">
            {confirm?.kind === "revoke" ? "Revoke" : "Issue new code"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
